import { Component, Vue } from 'vue-property-decorator';

import {
  ActionMenuItem,
  DisplayStatus,
  RewardItemDisplayDTO,
  RewardItemDTO,
  RewardPeriodDTO,
  RewardRequestDTO
} from '@/models';
import { MENU_ACTIONS, NOTIFICATION, PAGE_NAMES, REWARD_PERIOD_STATUSES, REWARD_REQUEST_STATUSES } from '@/constants';
import { routes } from '@/router';
import ExportRewardPeriodDetail from '@/services/export-reward-period-detail';
import {
  closeRewardPeriod,
  fetchRewardItemByPeriodId,
  fetchRewardPeriodById,
  fetchRewardRequestByPeriodId,
  publishRewardPeriod
} from '@/services';

import ActionMenu from '@/components/common/action-menu/ActionMenu.vue';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import Status from '@/components/common/status/Status.vue';
import RewardPeriodDetailItems from './reward-period-detail-items/RewardPeriodDetailItems.vue';
import RewardPeriodDetailRequests from './reward-period-detail-requests/RewardPeriodDetailRequests.vue';
import RewardPeriodDetailInformation from './reward-period-detail-information/RewardPeriodDetailInformation.vue';
import RewardPeriodDetailOverall from './reward-period-detail-overall/RewardPeriodDetailOverall.vue';

enum TabItem {
  Items = 'Items',
  Requests = 'Requests'
}

@Component({
  components: {
    ActionMenu,
    ConfirmationDialog,
    Status,
    RewardPeriodDetailItems,
    RewardPeriodDetailRequests,
    RewardPeriodDetailInformation,
    RewardPeriodDetailOverall
  }
})
export default class RewardPeriodDetail extends Vue {
  private tab = 0;
  private readonly tabItems = [
    { title: TabItem.Requests, component: RewardPeriodDetailRequests },
    { title: TabItem.Items, component: RewardPeriodDetailItems }
  ];
  private rewardPeriodId = -1;
  private isPublishDialogConfirmShown = false;
  private isCloseDialogConfirmShown = false;
  private rewardManagementRoute = routes.find(route => route.name === PAGE_NAMES.REWARD_MANAGEMENT);

  get rewardPeriod(): RewardPeriodDTO {
    return this.$store.getters['rewardPeriod/rewardPeriod'];
  }

  get rewardItems(): RewardItemDisplayDTO[] {
    return this.$store.getters['rewardPeriod/rewardItems'].map((item: RewardItemDTO) =>
      this.buildDisplayRewardItem(item)
    );
  }

  get rewardRequests(): RewardRequestDTO[] {
    return this.$store.getters['rewardPeriod/rewardRequests'];
  }

  buildDisplayRewardItem(rewardItem: RewardItemDTO): RewardItemDisplayDTO {
    return {
      id: rewardItem.id,
      prizeId: rewardItem.prize.id,
      name: rewardItem.prize.name,
      image: rewardItem.prize.image,
      description: rewardItem.prize.description,
      type: rewardItem.prize.type,
      cost: rewardItem.cost,
      quantity: rewardItem.quantity,
      redeemCount: rewardItem.redeemCount,
      maxRedeemPerUser: rewardItem.maxRedeemPerUser,
      estimatedPrice: rewardItem.estimatedPrice,
      createdAt: rewardItem.createdAt
    };
  }

  get displayStatus(): DisplayStatus {
    return REWARD_PERIOD_STATUSES[this.rewardPeriod.status];
  }

  get isPublishingRewardPeriod(): boolean {
    return this.rewardPeriod.status === REWARD_PERIOD_STATUSES.PUBLISHING.label;
  }

  get isUnPublishingRewardPeriod(): boolean {
    return this.rewardPeriod.status === REWARD_PERIOD_STATUSES.NOT_STARTED.label;
  }
  
  get menuItems(): ActionMenuItem[] {
    const menu = [] as ActionMenuItem[];
    menu.push({
      label: MENU_ACTIONS.EXPORT,
      icon: 'mdi-file-table',
      handler: () => {
        const exporter = new ExportRewardPeriodDetail(this.rewardPeriod, this.rewardItems, this.rewardRequests);
        exporter.generateXLSXFile();
      }
    });
    return menu;
  }

  async setInitialRewardPeriodById(): Promise<void> {
    const { data } = await fetchRewardPeriodById(this.rewardPeriodId);
    this.$store.commit('rewardPeriod/setRewardPeriodInformation', {
      id: data.id,
      name: data.name,
      description: data.description,
      status: data.status,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      startDate: data.startDate,
      endDate: data.endDate,
      createdBy: data.createdBy,
      settings: data.settings
    });
  }

  async setInitialRewardItems(): Promise<void> {
    const { data } = await fetchRewardItemByPeriodId(this.rewardPeriodId);
    const items = data.map(item => ({ ...item, periodRewardId: this.rewardPeriodId }));
    this.$store.commit('rewardPeriod/setRewardItems', items);
  }

  async setInitialRewardRequests(): Promise<void> {
    const { data } = await fetchRewardRequestByPeriodId(this.rewardPeriodId);

    this.$store.commit('rewardPeriod/setRewardRequests', data);
  }

  async publishRewardPeriod(): Promise<void> {
    this.isPublishDialogConfirmShown = false;
    const publishRewardPeriodResult = await publishRewardPeriod(this.rewardPeriodId);
    const dataNotification = {
      showing: true,
      type: '',
      title: '',
      content: [],
      icon: '',
      multiline: false,
      height: 0
    };
    if (publishRewardPeriodResult.data) {
      dataNotification.type = NOTIFICATION.TYPE.success;
      dataNotification.title = NOTIFICATION.TEXT.publishRewardPeriodSuccess;
      dataNotification.icon = NOTIFICATION.ICON.success;

      this.$store.commit('rewardPeriod/updateRewardPeriodStatus', REWARD_PERIOD_STATUSES.PUBLISHING.label);
    } else {
      dataNotification.type = NOTIFICATION.TYPE.error;
      dataNotification.title = NOTIFICATION.TEXT.publishRewardPeriodFailed;
      dataNotification.icon = NOTIFICATION.ICON.error;
    }
    this.$store.commit('notification/setNotification', dataNotification);
  }

  async closeRewardPeriod(): Promise<void> {
    this.isCloseDialogConfirmShown = false;
    const closeRewardPeriodResult = await closeRewardPeriod(this.rewardPeriodId);
    const dataNotification = {
      showing: true,
      type: '',
      title: '',
      content: [],
      icon: '',
      multiline: false,
      height: 0
    };
    if (closeRewardPeriodResult.data) {
      dataNotification.type = NOTIFICATION.TYPE.success;
      dataNotification.title = NOTIFICATION.TEXT.closeRewardPeriodSuccess;
      dataNotification.icon = NOTIFICATION.ICON.success;

      this.$store.commit('rewardPeriod/updateRewardPeriodStatus', REWARD_PERIOD_STATUSES.CLOSED.label);
    } else {
      dataNotification.type = NOTIFICATION.TYPE.error;
      dataNotification.title = NOTIFICATION.TEXT.closeRewardPeriodFailed;
      dataNotification.icon = NOTIFICATION.ICON.error;
    }
    this.$store.commit('notification/setNotification', dataNotification);
  }

  showPublishPeriodConfirmDialog(): void {
    this.isPublishDialogConfirmShown = true;
  }

  showClosePeriodConfirmDialog(): void {
    this.isCloseDialogConfirmShown = true;
  }

  async created(): Promise<void> {
    this.rewardPeriodId = parseInt(this.$route.params.id);
    this.tab = parseInt(this.$route.params?.tab) ?? 0;
    await this.setInitialRewardPeriodById();
    await this.setInitialRewardItems();
    await this.setInitialRewardRequests();
  }
}
